import { useEffect, useRef, useState } from 'react';
import { apiFetch } from '../../../context/AuthContext';
import { FILE_TYPE_MAP } from '../constants/fileTypes';
import { isSameUser } from '../utils/userHelpers';
import { resolveMediaSrc } from '../utils/resolveMediaSrc';

const API_BASE_URL = process.env.REACT_APP_BASE_URL || 'https://nexploy-eg.com';

const useMediaUpload = ({
  selectedUser,
  isConnected,
  messageInput,
  setMessageInput,
  setMessages,
  setConversations,
  currentUser,
  onlineUsers,
  socket,
  t,
}) => {
  const [uploading, setUploading] = useState(false);
  const objectUrlSetRef = useRef(new Set());

  useEffect(() => {
    return () => {
      objectUrlSetRef.current.forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {}
      });
      objectUrlSetRef.current.clear();
    };
  }, []);

  const sendMediaMessage = async (mediaData, mediaType) => {
    if (!selectedUser || !isConnected) return;

    const tempId = `temp-${Date.now()}`;
    const now = new Date();

    const tempMessage = {
      id: tempId,
      text: messageInput.trim() || null,
      type: mediaType,
      mediaUrl: mediaData.previewUrl || mediaData.url,
      thumbnailUrl: mediaData.thumbnailUrl || mediaData.previewUrl || mediaData.url,
      mediaType: mediaData.mimeType,
      mediaName: mediaData.name,
      imageWidth: mediaData.width,
      imageHeight: mediaData.height,
      mediaSize: mediaData.size,
      duration: mediaData.duration,
      time: now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      sent: true,
      status: 'sending',
    };

    setMessages((prev) => ({
      ...prev,
      [selectedUser.id]: [...(prev[selectedUser.id] || []), tempMessage],
    }));

    const caption = messageInput.trim();
    setMessageInput('');

    try {
      const response = await apiFetch(`${API_BASE_URL}/api/private-messages/messages/media`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipientId: selectedUser.id,
          storageKey: mediaData.storageKey || null,
          thumbnailKey: mediaData.thumbnailKey || null,
          mediaUrl: mediaData.url || null,
          mediaType,
          thumbnailUrl: mediaData.thumbnailUrl || null,
          width: mediaData.width,
          height: mediaData.height,
          size: mediaData.size,
          name: mediaData.name,
          mimeType: mediaData.mimeType,
          duration: mediaData.duration,
          caption: caption || null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to send media');
      }

      const result = await response.json();

      if (result.success) {
        setMessages((prev) => ({
          ...prev,
          [selectedUser.id]: prev[selectedUser.id].map((msg) =>
            msg.id === tempId
              ? {
                  ...msg,
                  id: result.data.id,
                  status: 'sent',
                  mediaUrl: resolveMediaSrc(result.data.mediaUrl) || msg.mediaUrl,
                  thumbnailUrl: resolveMediaSrc(result.data.thumbnailUrl) || msg.thumbnailUrl,
                }
              : msg
          ),
        }));

        try {
          if (mediaData.previewUrl) {
            URL.revokeObjectURL(mediaData.previewUrl);
            objectUrlSetRef.current.delete(mediaData.previewUrl);
          }
        } catch (e) {}

        setConversations((prev) => {
          const existingIndex = prev.findIndex((conv) => isSameUser(conv.id, selectedUser.id));
          const timestamp = now.getTime();

          let previewText;
          if (caption) {
            previewText = caption;
          } else {
            switch (mediaType) {
              case 'image': previewText = '📷 Image'; break;
              case 'voice': previewText = '🎤 Voice Message'; break;
              case 'pdf': previewText = '📄 PDF Document'; break;
              case 'excel': previewText = '📊 Excel File'; break;
              case 'word': previewText = '📝 Word Document'; break;
              case 'powerpoint': previewText = '📽️ PowerPoint'; break;
              default: previewText = 'File';
            }
          }

          const updatedConv = {
            // Spread existing conv first to preserve isGroup, isArchived, members, etc.
            ...(existingIndex >= 0 ? prev[existingIndex] : {}),
            id: selectedUser.id,
            name: selectedUser.name,
            avatar: selectedUser.avatar,
            lastMessage: previewText,
            time: t.justNow,
            timestamp,
            unread: 0,
            online: onlineUsers.has(selectedUser.id),
          };

          if (existingIndex >= 0) {
            const updated = [...prev];
            updated.splice(existingIndex, 1);
            return [updatedConv, ...updated];
          }

          return [updatedConv, ...prev];
        });
      }
    } catch (err) {
      console.error('Failed to send media:', err);
      setMessages((prev) => ({
        ...prev,
        [selectedUser.id]: prev[selectedUser.id].filter((msg) => msg.id !== tempId),
      }));
      alert(`${t.sendFailed}: ${err.message}`);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!selectedUser) {
      alert(t.userInfoMissing);
      return;
    }

    const fileInfo = FILE_TYPE_MAP[file.type];

    if (!fileInfo) {
      alert(`Unsupported file type: ${file.type}`);
      return;
    }

    const maxSizeMB = fileInfo.maxSize;
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    if (file.size > maxSizeBytes) {
      alert(`File size must be less than ${maxSizeMB}MB`);
      return;
    }

    try {
      setUploading(true);

      const previewUrl = URL.createObjectURL(file);
      objectUrlSetRef.current.add(previewUrl);

      // Upload to GCS via the backend upload endpoint
      const formData = new FormData();
      const isImage = fileInfo.type === 'image';
      const isVoice = fileInfo.type === 'voice';
      const endpoint = isImage ? 'upload/image' : isVoice ? 'upload/audio' : 'upload/pdf';
      formData.append(isImage ? 'image' : isVoice ? 'audio' : 'pdf', file);

      const uploadRes = await apiFetch(`${API_BASE_URL}/api/private-messages/${endpoint}`, {
        method: 'POST',
        body: formData,
      });

      if (!uploadRes.ok) {
        const err = await uploadRes.json().catch(() => ({}));
        throw new Error(err.error || err.message || 'Upload failed');
      }

      const { data: uploaded } = await uploadRes.json();

      let width = uploaded.width;
      let height = uploaded.height;

      // If the server didn't return dimensions (e.g. for non-sharp-processed images),
      // measure locally from the preview blob so the UI renders correctly.
      if (isImage && (!width || !height)) {
        const img = new Image();
        await new Promise((resolve) => {
          img.onload = resolve;
          img.src = previewUrl;
        });
        width = img.width;
        height = img.height;
      }

      const mediaData = {
        storageKey: uploaded.storageKey,
        thumbnailKey: uploaded.thumbnailKey || null,
        url: uploaded.url,
        thumbnailUrl: uploaded.thumbnailUrl || null,
        previewUrl,
        type: fileInfo.type,
        name: file.name,
        size: uploaded.size || file.size,
        width,
        height,
        mimeType: file.type,
        icon: fileInfo.icon,
      };

      await sendMediaMessage(mediaData, fileInfo.type);
    } catch (err) {
      alert(`Failed to upload file: ${err.message}`);
    } finally {
      setUploading(false);
      if (event.target) event.target.value = '';
    }
  };

  return { uploading, handleFileSelect, sendMediaMessage, objectUrlSetRef };
};

export default useMediaUpload;